import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API_BASE, formatApiErrorDetail } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input, Label, Textarea } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Wrench, Paperclip, AlertOctagon, Send, Download, MessageSquare, Save, Trash2 } from "lucide-react";
import { format, formatDistanceToNow, isBefore } from "date-fns";
import { cn } from "../lib/utils";

const priMap = { critical: "critical", high: "warning", medium: "warning", low: "success" };
const statusMap = {
  todo: { v: "neutral", label: "To do" },
  in_progress: { v: "info", label: "In progress" },
  in_review: { v: "warning", label: "In review" },
  blocked: { v: "critical", label: "Blocked" },
  done: { v: "success", label: "Done" },
  escalated: { v: "critical", label: "Escalated" },
};
const STATUSES = ["todo", "in_progress", "in_review", "blocked", "done"];

export default function RemediationDetailPage() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [users, setUsers] = useState([]);
  const [edit, setEdit] = useState(null);
  const [comment, setComment] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const { data } = await api.get(`/remediations/${id}`);
    setR(data);
    setEdit({
      priority: data.priority,
      status: data.status,
      owner_id: data.owner_id || "",
      progress: data.progress || 0,
      due_date: data.due_date ? data.due_date.slice(0, 10) : "",
    });
  };
  useEffect(() => {
    load();
    api.get("/users?limit=200").then(({ data }) => setUsers(data.items || [])).catch(() => {});
  }, [id]);

  if (!r) return <div className="h-40 bg-muted animate-pulse rounded" />;
  const overdue = r.due_date && r.status !== "done" && isBefore(new Date(r.due_date), new Date());

  const save = async () => {
    const payload = { ...edit };
    if (payload.due_date) payload.due_date = new Date(payload.due_date).toISOString();
    else delete payload.due_date;
    if (!payload.owner_id) delete payload.owner_id;
    payload.progress = Number(payload.progress);
    try {
      await api.patch(`/remediations/${id}`, payload);
      toast.success("Saved");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  };
  const escalate = async () => {
    const reason = window.prompt("Escalation reason (optional):", "");
    try {
      await api.post(`/remediations/${id}/escalate`, { reason });
      toast.success("Escalated — admins have been notified");
      load();
    } catch (err) { toast.error("Failed"); }
  };
  const sendComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/remediations/${id}/comments`, { text: comment.trim() });
      setComment("");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  };
  const uploadAttachment = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("File exceeds 10 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/remediations/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attached");
      load();
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const download = async (a) => {
    try {
      const token = localStorage.getItem("ccams_token");
      const resp = await fetch(`${API_BASE}/remediations/${id}/attachments/${a.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const el = document.createElement("a");
      el.href = url; el.download = a.filename;
      el.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("Download failed"); }
  };
  const delAttachment = async (a) => {
    if (!window.confirm("Delete attachment?")) return;
    await api.delete(`/remediations/${id}/attachments/${a.id}`);
    load();
  };

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <Link to="/remediations" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> All remediations
      </Link>

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            <div className="w-12 h-12 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <Wrench className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge variant={priMap[r.priority]}>{r.priority}</Badge>
                <Badge variant={statusMap[r.status]?.v || "neutral"}>{statusMap[r.status]?.label || r.status}</Badge>
                {r.escalation_level > 0 && <Badge variant="critical">Escalated L{r.escalation_level}</Badge>}
                {overdue && <Badge variant="critical">Overdue</Badge>}
              </div>
              <h1 className="text-2xl font-semibold tracking-tight">{r.title}</h1>
              {r.description && <p className="text-sm text-muted-foreground mt-2 whitespace-pre-line">{r.description}</p>}
              <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span>Owner: <span className="text-foreground font-medium">{r.owner_name || "—"}</span></span>
                <span>Created by: {r.creator_name || "—"}</span>
                {r.due_date && <span>Due: {format(new Date(r.due_date), "MMM d, yyyy")}</span>}
                {r.source_type && <span>Source: {r.source_type}</span>}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Button variant="destructive" size="sm" onClick={escalate} data-testid="rem-escalate">
                <AlertOctagon className="w-4 h-4" /> Escalate
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle>Update</CardTitle><CardDescription>Adjust progress and status.</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            {edit && <>
              <div>
                <Label>Progress: <span className="tabular-nums">{edit.progress}%</span></Label>
                <input data-testid="rem-progress" type="range" min="0" max="100" step="5" className="w-full mt-2"
                       value={edit.progress} onChange={(e) => setEdit({ ...edit, progress: Number(e.target.value) })} />
              </div>
              <div>
                <Label>Status</Label>
                <select data-testid="rem-status-edit" className="h-10 w-full mt-1.5 rounded-md border border-border bg-card px-3 text-sm"
                        value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })}>
                  {STATUSES.map((s) => <option key={s} value={s}>{statusMap[s].label}</option>)}
                </select>
              </div>
              <div>
                <Label>Priority</Label>
                <select className="h-10 w-full mt-1.5 rounded-md border border-border bg-card px-3 text-sm"
                        value={edit.priority} onChange={(e) => setEdit({ ...edit, priority: e.target.value })}>
                  {["critical", "high", "medium", "low"].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <Label>Owner</Label>
                <select className="h-10 w-full mt-1.5 rounded-md border border-border bg-card px-3 text-sm"
                        value={edit.owner_id} onChange={(e) => setEdit({ ...edit, owner_id: e.target.value })}>
                  <option value="">Unassigned</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Due date</Label>
                <Input type="date" className="mt-1.5" value={edit.due_date} onChange={(e) => setEdit({ ...edit, due_date: e.target.value })} />
              </div>
              <Button className="w-full" onClick={save} data-testid="rem-save"><Save className="w-4 h-4" /> Save changes</Button>
            </>}
          </CardContent>
        </Card>

        <div className="lg:col-span-2 space-y-4">
          {/* Comments */}
          <Card>
            <CardHeader><CardTitle>Comments</CardTitle><CardDescription>Discussion thread on this remediation.</CardDescription></CardHeader>
            <CardContent>
              <div className="space-y-3 mb-4">
                {r.comments.length === 0 && <div className="text-sm text-muted-foreground text-center py-4">No comments yet.</div>}
                {r.comments.map((c) => (
                  <div key={c.id} className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-subtle text-primary flex items-center justify-center text-xs font-semibold shrink-0">
                      {(c.author_name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm font-medium">{c.author_name}</span>
                        <span className="text-xs text-muted-foreground">{c.at && formatDistanceToNow(new Date(c.at), { addSuffix: true })}</span>
                      </div>
                      <div className="text-sm mt-0.5 whitespace-pre-line">{c.text}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Textarea data-testid="rem-comment-input" value={comment} onChange={(e) => setComment(e.target.value)}
                          placeholder="Write a comment…" rows={2} className="flex-1" />
                <Button onClick={sendComment} data-testid="rem-comment-send"><Send className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>

          {/* Attachments */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Attachments</CardTitle>
                  <CardDescription>Supporting evidence and files (max 10 MB each).</CardDescription>
                </div>
                <div>
                  <input ref={fileRef} type="file" className="hidden" onChange={(e) => uploadAttachment(e.target.files?.[0])} data-testid="rem-attach-input" />
                  <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="rem-attach-btn">
                    <Paperclip className="w-4 h-4" /> {uploading ? "Uploading…" : "Attach file"}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ul className="divide-y divide-border">
                {r.attachments.length === 0 && <li className="px-6 py-6 text-center text-sm text-muted-foreground">No attachments.</li>}
                {r.attachments.map((a) => (
                  <li key={a.id} className="px-6 py-3 flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{a.filename}</div>
                      <div className="text-xs text-muted-foreground">
                        {a.uploaded_by_name} · {a.size && `${Math.round(a.size / 1024)} KB`}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="outline" onClick={() => download(a)} data-testid={`rem-attach-download-${a.id}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="destructive" onClick={() => delAttachment(a)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
